import { useQuery, type QueryKey } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useSuiteRunsTableRowActions } from '@/features/executions/components/useSuiteRunsTableRowActions';
import { useTestRunsTableRowActions } from '@/features/executions/components/useTestRunsTableRowActions';
import { queryTestRuns } from '@/features/executions/data';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import {
  buildSuiteRunPath,
  buildTestRunPath,
} from '@/features/shared/components/tableSystem/tableRoutes';
import { TableV2DurationCell } from '@/features/shared/components/tableSystem/TableV2DurationCell';
import {
  queryAllTableV2NestedRows,
  tableV2NestedCarrierRowSx,
  tableV2NestedContentSx,
  tableV2NestedDepthSx,
} from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2ActionsHeaderIcon,
  TableV2ChevronButton,
  TableV2DateTimeCell,
  TableV2NestedCollapse,
  TableV2NameCell,
  TableV2PassRateCell,
  TableV2StatusChip,
  TableV2TagGroup,
  tableV2ActionsColumnWidth,
  tableV2BodyRowHeightPx,
  tableV2ChevronCellSx,
  tableV2ChevronColumnWidth,
  tableV2NestedBodyRowHeightPx,
  type TableV2Counts,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { TableV2QueryShell } from '@/features/shared/components/tableSystem/TableV2QueryShell';
import { formatTableV2RunNumber } from '@/features/shared/components/tableSystem/TableV2RunFormatting';
import {
  TableV2SkeletonRow,
  type TableV2SkeletonColumnKind,
} from '@/features/shared/components/tableSystem/TableV2Skeleton';
import { resolveTableV2NestedSkeletonRowCount } from '@/features/shared/components/tableSystem/TableV2SkeletonRows';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { gridPaths, queryKeys } from '@/shared/hooks';
import { s } from '@/theme/tokens';
import { buildEqFilter } from '@/ui/composites/grid/gridFilters';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Text } from '@/ui/primitives';
import type { TagColor } from '@/shared/data/tags';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryResult, SuiteRunDto, TestRunDto } from '@flowqa/shared';

type SuiteRunsTableV2Props = {
  active: boolean;
  actionsDisabled?: boolean;
  fixedFilters?: GridQueryInput['filters'];
  forceLoading?: boolean;
  linksDisabled?: boolean;
  nameHeaderLabel?: string;
  noRowsText?: string;
  queryFn: (input: GridQueryInput) => Promise<QueryResult<SuiteRunDto>>;
  queryKey: QueryKey;
  requireAuth?: boolean;
  search?: string;
  showSuiteName?: boolean;
  testRunsQueryFn?: (input: GridQueryInput) => Promise<QueryResult<TestRunDto>>;
  onQueryMeta?: (meta: { total: number; items: SuiteRunDto[] } | null) => void;
};

const suiteRunV2Sorts = [{ field: 'timestamp', direction: 'desc' }] satisfies ApiSort[];
const skeletonColumns = [
  'chevron',
  'name',
  'status',
  'passRate',
  'duration',
  'dateTime',
  'tags',
  'actions',
] satisfies TableV2SkeletonColumnKind[];
const testRunSkeletonColumns = [
  'name',
  'status',
  'duration',
  'dateTime',
  'tags',
  'actions',
] satisfies TableV2SkeletonColumnKind[];

const countSuiteRunTests = (row: SuiteRunDto): TableV2Counts => {
  const passed = Math.max(
    0,
    (row.tests ?? 0) - (row.failures ?? 0) - (row.errors ?? 0) - (row.skipped ?? 0),
  );
  return {
    total: row.tests ?? 0,
    passed,
    failed: row.failures ?? 0,
    errored: row.errors ?? 0,
    skipped: row.skipped ?? 0,
    untested: 0,
  };
};

const colgroup = (
  <colgroup>
    <col width={tableV2ChevronColumnWidth} />
    <col width="24%" />
    <col width="10%" />
    <col width="16%" />
    <col width="12%" />
    <col width="16%" />
    <col width="16%" />
    <col width={tableV2ActionsColumnWidth} />
  </colgroup>
);

const testRunColgroup = (
  <colgroup>
    <col width="30%" />
    <col width="13%" />
    <col width="13%" />
    <col width="20%" />
    <col width="20%" />
    <col width={tableV2ActionsColumnWidth} />
  </colgroup>
);

const header = (
  <TableRow>
    <TableCell sx={{ p: s('none'), width: tableV2ChevronColumnWidth }} />
    <TableCell>Suite Run</TableCell>
    <TableCell>Status</TableCell>
    <TableCell>Pass Rate</TableCell>
    <TableCell>Duration</TableCell>
    <TableCell>Ran At</TableCell>
    <TableCell>Tags</TableCell>
    <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
      <TableV2ActionsHeaderIcon />
    </TableCell>
  </TableRow>
);

const buildHeader = (nameHeaderLabel: string): ReactElement => (
  <TableRow>
    <TableCell sx={{ p: s('none'), width: tableV2ChevronColumnWidth }} />
    <TableCell>{nameHeaderLabel}</TableCell>
    <TableCell>Status</TableCell>
    <TableCell>Pass Rate</TableCell>
    <TableCell>Duration</TableCell>
    <TableCell>Ran At</TableCell>
    <TableCell>Tags</TableCell>
    <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
      <TableV2ActionsHeaderIcon />
    </TableCell>
  </TableRow>
);

const testRunHeader = (
  <TableRow>
    <TableCell>Test Run</TableCell>
    <TableCell>Status</TableCell>
    <TableCell>Duration</TableCell>
    <TableCell>Ran At</TableCell>
    <TableCell>Tags</TableCell>
    <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
      <TableV2ActionsHeaderIcon />
    </TableCell>
  </TableRow>
);

const TestRunNestedRow = ({
  actionsDisabled,
  deleteRowPendingId,
  linksDisabled,
  row,
  updateRowTagsColorPendingId,
  onDeleteRow,
  onUpdateRowTagsColor,
}: {
  actionsDisabled: boolean;
  deleteRowPendingId: string | null;
  linksDisabled: boolean;
  row: TestRunDto;
  updateRowTagsColorPendingId: string | null;
  onDeleteRow: (row: TestRunDto) => void;
  onUpdateRowTagsColor: (input: { row: TestRunDto; tags: string[]; colors: TagColor[] }) => void;
}): ReactElement => (
  <TableRow
    sx={[
      tableV2NestedDepthSx[1].bodyRow,
      {
        '& > td': {
          borderBottom: (theme) => `1px solid ${theme.app.colors.border}`,
          height: `${String(tableV2NestedBodyRowHeightPx)}px`,
          py: s('sm'),
          verticalAlign: 'middle',
        },
      },
    ]}
  >
    <TableCell>
      <TableV2NameCell
        href={linksDisabled ? null : buildTestRunPath(row.testSlug ?? row.testId, row.runNumber)}
        primary={row.displayName ?? row.testName ?? row.name ?? row.id}
        secondary={formatTableV2RunNumber(row.runNumber)}
        secondaryHref={
          linksDisabled ? null : buildTestRunPath(row.testSlug ?? row.testId, row.runNumber)
        }
      />
    </TableCell>
    <TableCell>
      <TableV2StatusChip status={row.status} />
    </TableCell>
    <TableCell>
      <TableV2DurationCell value={row.durationMs} />
    </TableCell>
    <TableCell>
      <TableV2DateTimeCell value={row.timestamp} />
    </TableCell>
    <TableCell>
      <TableV2TagGroup tags={row.tags ?? []} />
    </TableCell>
    <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
      {actionsDisabled ? null : (
        <TableActionsCell
          rowName={row.displayName ?? row.testName ?? row.name ?? row.id}
          verticalAlign="center"
          deleteActionLabel={getEntityActionLabel('delete', 'Test Run')}
          editTagsEntityNoun="test run"
          rowTags={row.tags ?? []}
          onUpdateTagsColor={(input) => {
            onUpdateRowTagsColor({ row, ...input });
          }}
          updateTagsColorLoading={updateRowTagsColorPendingId === row.id}
          onDelete={() => {
            onDeleteRow(row);
          }}
          disableDelete={deleteRowPendingId === row.id}
        />
      )}
    </TableCell>
  </TableRow>
);

const SuiteRunExpandedTests = ({
  actionsDisabled,
  childCount,
  deleteRowPendingId,
  expanded,
  linksDisabled,
  organisationId,
  projectId,
  suiteRunId,
  testRunsQueryFn,
  updateRowTagsColorPendingId,
  onDeleteTestRunRow,
  onUpdateTestRunRowTagsColor,
}: {
  actionsDisabled: boolean;
  childCount: number;
  deleteRowPendingId: string | null;
  expanded: boolean;
  linksDisabled: boolean;
  organisationId: string | null;
  projectId: string;
  suiteRunId: string;
  testRunsQueryFn?: (input: GridQueryInput) => Promise<QueryResult<TestRunDto>>;
  updateRowTagsColorPendingId: string | null;
  onDeleteTestRunRow: (row: TestRunDto) => void;
  onUpdateTestRunRowTagsColor: (input: {
    row: TestRunDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
}): ReactElement => {
  const query = useQuery({
    queryKey: queryKeys.dataGrid(
      gridPaths.testRuns,
      'v2-nested',
      organisationId,
      projectId,
      suiteRunId,
    ),
    enabled: expanded,
    queryFn: async () => {
      const input = {
        page: 1,
        pageSize: 1,
        sorts: [{ field: 'timestamp', direction: 'desc' as const }],
        filters: buildEqFilter('suiteRunId', suiteRunId),
      };
      return await queryAllTableV2NestedRows(
        input,
        testRunsQueryFn ??
          (async (nextInput) => await queryTestRuns(projectId, organisationId, nextInput)),
      );
    },
    staleTime: 30_000,
  });
  const skeletonRowCount = resolveTableV2NestedSkeletonRowCount(childCount);

  return (
    <TableV2NestedCollapse expanded={expanded}>
      <Box sx={tableV2NestedContentSx}>
        {query.isLoading ? (
          <Box sx={tableV2NestedDepthSx[1].frame}>
            <Table aria-label="Test runs loading" sx={tableV2NestedDepthSx[1].table}>
              {testRunColgroup}
              <TableHead>{testRunHeader}</TableHead>
              <TableBody>
                {Array.from({ length: skeletonRowCount }, (_item, index) => (
                  <TableV2SkeletonRow
                    key={`test-runs-nested-skeleton-${String(index)}`}
                    columns={testRunSkeletonColumns}
                    isLastVisibleRow={index === skeletonRowCount - 1}
                    rowIndex={index}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : query.data && query.data.items.length > 0 ? (
          <Box sx={tableV2NestedDepthSx[1].frame}>
            <Table aria-label="Test runs" sx={tableV2NestedDepthSx[1].table}>
              {testRunColgroup}
              <TableHead>{testRunHeader}</TableHead>
              <TableBody>
                {query.data.items.map((testRun) => (
                  <TestRunNestedRow
                    key={testRun.id}
                    actionsDisabled={actionsDisabled}
                    deleteRowPendingId={deleteRowPendingId}
                    linksDisabled={linksDisabled}
                    row={testRun}
                    updateRowTagsColorPendingId={updateRowTagsColorPendingId}
                    onDeleteRow={onDeleteTestRunRow}
                    onUpdateRowTagsColor={onUpdateTestRunRowTagsColor}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Text role="body" color="textSecondary">
            No data
          </Text>
        )}
      </Box>
    </TableV2NestedCollapse>
  );
};

const SuiteRunRow = ({
  actionsDisabled,
  deleteRowPendingId,
  isLastVisibleRow,
  linksDisabled,
  row,
  showSuiteName,
  testRunDeleteRowPendingId,
  testRunUpdateRowTagsColorPendingId,
  testRunsQueryFn,
  updateRowTagsColorPendingId,
  onDeleteRow,
  onDeleteTestRunRow,
  onUpdateRowTagsColor,
  onUpdateTestRunRowTagsColor,
}: {
  actionsDisabled: boolean;
  deleteRowPendingId: string | null;
  isLastVisibleRow: boolean;
  linksDisabled: boolean;
  row: SuiteRunDto;
  showSuiteName: boolean;
  testRunDeleteRowPendingId: string | null;
  testRunUpdateRowTagsColorPendingId: string | null;
  testRunsQueryFn?: (input: GridQueryInput) => Promise<QueryResult<TestRunDto>>;
  updateRowTagsColorPendingId: string | null;
  onDeleteRow: (row: SuiteRunDto) => void;
  onDeleteTestRunRow: (row: TestRunDto) => void;
  onUpdateRowTagsColor: (input: { row: SuiteRunDto; tags: string[]; colors: TagColor[] }) => void;
  onUpdateTestRunRowTagsColor: (input: {
    row: TestRunDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
}): ReactElement => {
  const [expanded, setExpanded] = useState(false);
  const name = row.displayName ?? row.suiteName ?? row.name ?? row.id;
  const runPath = buildSuiteRunPath(row.suiteSlug ?? row.suiteId, row.runNumber);
  const runNumberLabel = formatTableV2RunNumber(row.runNumber);
  const primaryLabel = showSuiteName ? name : (runNumberLabel ?? name);
  const secondaryLabel = showSuiteName ? runNumberLabel : null;
  const secondaryHref = showSuiteName ? runPath : null;
  const testCount = countSuiteRunTests(row).total;

  return (
    <>
      <TableRow
        sx={{
          backgroundColor: 'background.paper',
          '& > td': {
            borderBottom:
              expanded || isLastVisibleRow ? 0 : (theme) => `1px solid ${theme.app.colors.border}`,
            height: `${String(tableV2BodyRowHeightPx)}px`,
            py: s('md'),
            verticalAlign: 'middle',
          },
        }}
      >
        <TableCell align="center" sx={tableV2ChevronCellSx}>
          <TableV2ChevronButton
            expanded={expanded}
            label={expanded ? 'Collapse test runs' : 'Expand test runs'}
            onClick={() => {
              setExpanded((current) => !current);
            }}
          />
        </TableCell>
        <TableCell>
          <TableV2NameCell
            href={linksDisabled ? null : runPath}
            primary={primaryLabel}
            secondary={secondaryLabel}
            secondaryHref={linksDisabled ? null : secondaryHref}
          />
        </TableCell>
        <TableCell>
          <TableV2StatusChip status={row.status} />
        </TableCell>
        <TableCell>
          <TableV2PassRateCell counts={countSuiteRunTests(row)} />
        </TableCell>
        <TableCell>
          <TableV2DurationCell value={row.durationMs} />
        </TableCell>
        <TableCell>
          <TableV2DateTimeCell value={row.timestamp} />
        </TableCell>
        <TableCell>
          <TableV2TagGroup tags={row.tags ?? []} />
        </TableCell>
        <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
          {actionsDisabled ? null : (
            <TableActionsCell
              rowName={name}
              verticalAlign="center"
              deleteActionLabel={getEntityActionLabel('delete', 'Suite Run')}
              editTagsEntityNoun="suite run"
              rowTags={row.tags ?? []}
              onUpdateTagsColor={(input) => {
                onUpdateRowTagsColor({ row, ...input });
              }}
              updateTagsColorLoading={updateRowTagsColorPendingId === row.id}
              onDelete={() => {
                onDeleteRow(row);
              }}
              disableDelete={deleteRowPendingId === row.id}
            />
          )}
        </TableCell>
      </TableRow>
      <TableRow
        sx={[
          tableV2NestedCarrierRowSx,
          expanded && !isLastVisibleRow
            ? {
                '& > td': {
                  borderBottom: (theme) => `1px solid ${theme.app.colors.border}`,
                },
              }
            : {},
        ]}
      >
        <TableCell colSpan={8}>
          <SuiteRunExpandedTests
            actionsDisabled={actionsDisabled}
            childCount={testCount}
            deleteRowPendingId={testRunDeleteRowPendingId}
            expanded={expanded}
            linksDisabled={linksDisabled}
            organisationId={row.organisationId}
            projectId={row.projectId}
            suiteRunId={row.id}
            testRunsQueryFn={testRunsQueryFn}
            updateRowTagsColorPendingId={testRunUpdateRowTagsColorPendingId}
            onDeleteTestRunRow={onDeleteTestRunRow}
            onUpdateTestRunRowTagsColor={onUpdateTestRunRowTagsColor}
          />
        </TableCell>
      </TableRow>
    </>
  );
};

export const SuiteRunsTableV2 = ({
  actionsDisabled = false,
  active,
  fixedFilters,
  forceLoading = false,
  linksDisabled = false,
  nameHeaderLabel = 'Suite Run',
  noRowsText = 'No data',
  onQueryMeta,
  queryFn,
  queryKey,
  requireAuth = true,
  search,
  showSuiteName = true,
  testRunsQueryFn,
}: SuiteRunsTableV2Props): ReactElement => {
  const { selectedProjectId } = useProjectFilter();
  const {
    deleteLoading,
    deleteRowPendingId,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId,
  } = useSuiteRunsTableRowActions({ selectedProjectId });
  const {
    deleteLoading: testRunDeleteLoading,
    deleteRowPendingId: testRunDeleteRowPendingId,
    deleteTarget: testRunDeleteTarget,
    onDeleteClose: onTestRunDeleteClose,
    onDeleteConfirm: onTestRunDeleteConfirm,
    onDeleteRow: onDeleteTestRunRow,
    onUpdateRowTagsColor: onUpdateTestRunRowTagsColor,
    updateRowTagsColorPendingId: testRunUpdateRowTagsColorPendingId,
  } = useTestRunsTableRowActions({ selectedProjectId });

  return (
    <>
      <TableV2QueryShell
        active={active}
        ariaLabel="Suite runs"
        colgroup={colgroup}
        emptyText={noRowsText}
        entityLabel="suite runs"
        fixedFilters={fixedFilters}
        forceLoading={forceLoading}
        header={nameHeaderLabel === 'Suite Run' ? header : buildHeader(nameHeaderLabel)}
        queryFn={queryFn}
        queryKey={queryKey}
        requireAuth={requireAuth}
        search={search}
        skeletonColumns={skeletonColumns}
        sorts={suiteRunV2Sorts}
        onQueryMeta={onQueryMeta}
        renderRow={(row, context) => (
          <SuiteRunRow
            key={row.id}
            actionsDisabled={actionsDisabled}
            deleteRowPendingId={deleteRowPendingId}
            isLastVisibleRow={context.isLastVisibleRow}
            linksDisabled={linksDisabled}
            row={row}
            showSuiteName={showSuiteName}
            testRunDeleteRowPendingId={testRunDeleteRowPendingId}
            testRunUpdateRowTagsColorPendingId={testRunUpdateRowTagsColorPendingId}
            testRunsQueryFn={testRunsQueryFn}
            updateRowTagsColorPendingId={updateRowTagsColorPendingId}
            onDeleteRow={onDeleteRow}
            onDeleteTestRunRow={onDeleteTestRunRow}
            onUpdateRowTagsColor={onUpdateRowTagsColor}
            onUpdateTestRunRowTagsColor={onUpdateTestRunRowTagsColor}
          />
        )}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'suiteRun' })}
        description={getDialogMessage('destructive.delete.simpleDescription', {
          entity: 'suiteRun',
        })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'suiteRun',
        })}
        confirmColor="error"
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
      <ConfirmDialog
        open={testRunDeleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'testRun' })}
        description={getDialogMessage('destructive.delete.simpleDescription', {
          entity: 'testRun',
        })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'testRun',
        })}
        confirmColor="error"
        loading={testRunDeleteLoading}
        onClose={onTestRunDeleteClose}
        onConfirm={onTestRunDeleteConfirm}
      />
    </>
  );
};
